// components/AITutor.js
import React, { useState, useEffect } from 'react';
import { useAI } from '../contexts/AIContext';

const AITutor = ({ onBack }) => {
  const [currentLesson, setCurrentLesson] = useState('speaking');
  const [messages, setMessages] = useState([
    { type: 'ai', content: 'Hi! 我是您的专属AI老师，我可以帮助您学习英语!', timestamp: new Date() },
    { type: 'ai', content: '今天我们要学习什么呢？', timestamp: new Date() }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  
  const { 
    getTutorResponse, 
    addToConversation, 
    isLoading, 
    error, 
    clearError 
  } = useAI();

  const lessons = [
    { id: 'speaking', name: '口语练习', icon: '🗣️' },
    { id: 'writing', name: '写作指导', icon: '✍️' },
    { id: 'grammar', name: '语法学习', icon: '📝' },
    { id: 'vocabulary', name: '词汇扩展', icon: '📚' }
  ];

  const speakText = (text) => {
    if ('speechSynthesis' in window) {
      const voices = window.speechSynthesis.getVoices();
      let selectedVoice = voices.find(voice => 
        voice.lang.startsWith('en') && 
        (voice.name.includes('Google') || voice.name.includes('Microsoft') || voice.name.includes('Neural'))
      ) || voices.find(voice => voice.lang.startsWith('en-US')) || voices[0];
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.voice = selectedVoice;
      utterance.rate = 0.8; // Natural speaking pace
      utterance.pitch = 1.0;
      utterance.volume = 0.9;
      utterance.lang = 'en-US';
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      utterance.onerror = (event) => {
        console.error('AI voice error:', event.error);
        setIsSpeaking(false);
      };
      
      window.speechSynthesis.speak(utterance);
    }
  };

  const sendMessage = async () => {
    if (inputMessage.trim()) {
      const userMessage = inputMessage;
      setInputMessage('');
      
      // Add user message immediately
      const newMessages = [...messages, { type: 'user', content: userMessage, timestamp: new Date() }];
      setMessages(newMessages);
      setIsTyping(true);
      clearError();
      
      try {
        // Get AI response
        const aiResponse = await getTutorResponse(userMessage, {
          currentLesson,
          previousMessages: messages.slice(-3) // Last 3 messages for context
        });
        
        // Add AI response
        const updatedMessages = [...newMessages, { type: 'ai', content: aiResponse, timestamp: new Date() }];
        setMessages(updatedMessages);
        
        // Add to conversation history
        addToConversation(userMessage, aiResponse);
        
        // Speak the AI response
        setTimeout(() => speakText(aiResponse), 500);
        
      } catch (err) {
        console.error('AI Error:', err);
        const errorMessage = 'Sorry, I encountered an error. Please try again.';
        setMessages([...newMessages, { type: 'ai', content: errorMessage, timestamp: new Date() }]);
      } finally {
        setIsTyping(false);
      }
    }
  };

  return (
    <div className="ai-tutor">
      <header>
        <button onClick={onBack}>← Back</button>
        <h1>AI 1对1 Tutor</h1>
      </header>
      
      <div className="tutor-interface">
        <div className="ai-avatar">
          <div className="avatar-image">🤖</div>
          <h2>AI English Tutor</h2>
          <p>Personalized Learning Experience</p>
        </div>
        
        <div className="lesson-categories">
          {lessons.map(lesson => (
            <button
              key={lesson.id}
              className={`lesson-btn ${currentLesson === lesson.id ? 'active' : ''}`}
              onClick={() => setCurrentLesson(lesson.id)}
            >
              <span className="lesson-icon">{lesson.icon}</span>
              <span className="lesson-name">{lesson.name}</span>
            </button>
          ))}
        </div>
        
        <div className="chat-interface">
          {error && (
            <div className="error-message">
              <span>⚠️ {error}</span>
              <button onClick={clearError}>✕</button>
            </div>
          )}
          
          <div className="messages">
            {messages.map((message, index) => (
              <div key={index} className={`message ${message.type}`}>
                <div className="message-content">{message.content}</div>
                {message.type === 'ai' && (
                  <button 
                    className="speak-btn"
                    onClick={() => speakText(message.content)}
                    disabled={isSpeaking}
                  >
                    {isSpeaking ? '🔊 Speaking...' : '🔊 Speak'}
                  </button>
                )}
              </div>
            ))}
            
            {isTyping && (
              <div className="message ai typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                  AI is typing...
                </div>
              </div>
            )}
          </div>
          
          <div className="input-area">
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your question here..."
              onKeyPress={(e) => e.key === 'Enter' && !isTyping && sendMessage()}
              disabled={isTyping}
            />
            <button 
              onClick={sendMessage}
              disabled={isTyping || !inputMessage.trim()}
            >
              {isTyping ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
        
        <div className="ai-features">
          <h3>AI Features</h3>
          <div className="features-grid">
            <div className="feature-item">
              <span className="feature-icon">🎯</span>
              <span>Personalized Learning Path</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">📈</span>
              <span>Progress Tracking</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">🔄</span>
              <span>Adaptive Difficulty</span>
            </div>
            <div className="feature-item">
              <span className="feature-icon">⏰</span>
              <span>Smart Scheduling</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AITutor;